import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Activity,
  BarChart3,
  FolderTree,
  LayoutDashboard,
  ListPlus,
  Loader2,
  MonitorSmartphone,
  PackageCheck,
  Printer,
  Search,
  Settings2,
  ShoppingBag,
  ShoppingCart,
  Store,
  UserRound,
  Users,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import globalSearchApi from '../../api/globalSearchApi';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import '../../styles/components/global-search.css';

const SUPER_ADMIN_ROLES = [
  'superadmin',
  'superadministrator',
];

const MANAGER_ROLES = [
  'manager',
  'restaurantmanager',
  'restaurantmanagerrole',
  'admin',
  'administrator',
];

const ORDERS_ONLY_ROLES = [
  'waiter',
  'serveur',
  'cuisini',
  'cuisine',
  'kitchen',
  'cuisiner',
  'cuisinier',
];

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const extractRoleValue = (role) => {
  if (!role) return '';

  if (
    typeof role === 'string' ||
    typeof role === 'number'
  ) {
    return role;
  }

  if (typeof role === 'object') {
    return (
      role.name ||
      role.slug ||
      role.code ||
      role.value ||
      role.key ||
      role.role ||
      ''
    );
  }

  return '';
};

const getCurrentUserRole = (user) => {
  const possibleRoles = [
    user?.role?.name,
    user?.role?.slug,
    user?.role?.code,
    user?.role?.value,
    user?.role,
    user?.data?.role?.name,
    user?.data?.role?.slug,
    user?.data?.role?.code,
    user?.data?.role?.value,
    user?.data?.role,
    user?.user?.role?.name,
    user?.user?.role?.slug,
    user?.user?.role?.code,
    user?.user?.role?.value,
    user?.user?.role,
    user?.account?.role?.name,
    user?.account?.role,
    user?.profile?.role?.name,
    user?.profile?.role,
    user?.roles?.[0]?.name,
    user?.roles?.[0]?.slug,
    user?.roles?.[0]?.code,
    user?.roles?.[0]?.value,
    user?.roles?.[0],
  ];

  for (const possibleRole of possibleRoles) {
    const roleValue = extractRoleValue(possibleRole);

    if (roleValue) {
      return roleValue;
    }
  }

  return '';
};

const getSearchMode = (role) => {
  const normalizedRole = normalizeRole(role);

  if (SUPER_ADMIN_ROLES.includes(normalizedRole)) {
    return 'superAdmin';
  }

  if (MANAGER_ROLES.includes(normalizedRole)) {
    return 'manager';
  }

  if (ORDERS_ONLY_ROLES.includes(normalizedRole)) {
    return 'ordersOnly';
  }

  return 'limited';
};

const getCurrentEstablishmentId = (user) => {
  return (
    localStorage.getItem('selected_establishment_id') ||
    user?.establishment_id ||
    user?.data?.establishment_id ||
    user?.user?.establishment_id ||
    user?.establishment?.id ||
    user?.data?.establishment?.id ||
    user?.user?.establishment?.id ||
    user?.restaurant?.id ||
    user?.managed_establishment_id ||
    user?.managedEstablishment?.id ||
    1
  );
};

const getDefaultLocale = (user) => {
  return (
    user?.establishment?.default_language ||
    user?.data?.establishment?.default_language ||
    user?.user?.establishment?.default_language ||
    'fr'
  );
};

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const normalizeSearchText = (value) => {
  return String(value || '')
    .trim()
    .toLocaleLowerCase();
};

const getSearchableText = (item) => {
  return normalizeSearchText(
    [
      item.label,
      item.path,
      ...(item.keywords || []),
    ].join(' '),
  );
};

const uniqueResults = (results) => {
  const seen = new Set();

  return results.filter((result) => {
    const key = `${result.kind}:${result.id || result.path}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

function GlobalSearch() {
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchRequestRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, getFeature } = useAuth();
  const { t, language } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);

  const currentUserRole = getCurrentUserRole(user);
  const searchMode = getSearchMode(currentUserRole);
  const establishmentId = getCurrentEstablishmentId(user);
  const defaultLocale = getDefaultLocale(user);

  const navigationItems = useMemo(() => {
    const dashboardItem = {
      id: 'page-dashboard',
      kind: 'page',
      label: translateFallback(t, 'sidebar.dashboard', 'Dashboard'),
      path: '/dashboard',
      icon: LayoutDashboard,
      keywords: ['home', 'overview'],
    };

    const accountItem = {
      id: 'page-account',
      kind: 'page',
      label: t('header.profile'),
      path: '/account-settings',
      icon: UserRound,
      keywords: ['account', 'profile', 'settings'],
    };

    if (searchMode === 'superAdmin') {
      return [
        dashboardItem,
        {
          id: 'page-users',
          kind: 'page',
          label: translateFallback(t, 'sidebar.users', 'Users'),
          path: '/users',
          icon: Users,
          keywords: ['manager', 'managers', 'owner', 'owners'],
        },
        {
          id: 'page-establishments',
          kind: 'page',
          label: translateFallback(
            t,
            'sidebar.establishments',
            'Establishments',
          ),
          path: '/establishments',
          icon: Store,
          keywords: ['restaurant', 'restaurants', 'business', 'businesses'],
        },
        {
          id: 'page-platform-features',
          kind: 'page',
          label: translateFallback(
            t,
            'sidebar.platformFeatures',
            'Platform features',
          ),
          path: '/platform-features',
          icon: Settings2,
          keywords: ['features', 'permissions', 'access'],
        },
        {
          id: 'page-activity',
          kind: 'page',
          label: translateFallback(
            t,
            'sidebar.activityTracking',
            'Activity tracking',
          ),
          path: '/activity-tracking',
          icon: Activity,
          keywords: ['logs', 'activity', 'history'],
        },
        {
          id: 'page-qr-products',
          kind: 'page',
          label: translateFallback(
            t,
            'sidebar.qrPrintProducts',
            'QR products',
          ),
          path: '/qr-print/products',
          icon: Printer,
          keywords: ['print', 'qr', 'nfc'],
        },
        {
          id: 'page-qr-orders',
          kind: 'page',
          label: translateFallback(
            t,
            'sidebar.qrPrintOrders',
            'Print orders',
          ),
          path: '/qr-print/orders',
          icon: PackageCheck,
          keywords: ['print', 'orders', 'quotation'],
        },
        accountItem,
      ];
    }

    if (searchMode === 'ordersOnly') {
      return [
        {
          id: 'page-orders',
          kind: 'page',
          label: translateFallback(t, 'sidebar.orders', 'Orders'),
          path: '/orders',
          icon: ShoppingBag,
          keywords: ['order', 'table', 'customer'],
        },
        accountItem,
      ];
    }

    if (searchMode !== 'manager') {
      return [accountItem];
    }

    const items = [
      dashboardItem,
      {
        id: 'page-menus',
        kind: 'page',
        label: translateFallback(t, 'sidebar.menuDesign', 'Menus'),
        path: '/menus',
        icon: MonitorSmartphone,
        keywords: ['menu', 'menus', 'design'],
      },
      {
        id: 'page-categories',
        kind: 'page',
        label: translateFallback(t, 'sidebar.categories', 'Categories'),
        path: '/categories',
        icon: FolderTree,
        keywords: ['category', 'categories'],
      },
      {
        id: 'page-products',
        kind: 'page',
        label: translateFallback(t, 'sidebar.products', 'Products'),
        path: '/products',
        icon: UtensilsCrossed,
        keywords: ['product', 'products', 'dish', 'dishes', 'item'],
      },
      {
        id: 'page-supplements',
        kind: 'page',
        label: translateFallback(t, 'sidebar.supplements', 'Supplements'),
        path: '/supplements',
        icon: ListPlus,
        keywords: ['supplement', 'supplements', 'extra', 'extras'],
      },
      {
        id: 'page-orders',
        kind: 'page',
        label: translateFallback(t, 'sidebar.orders', 'Orders'),
        path: '/orders',
        icon: ShoppingBag,
        keywords: ['order', 'orders', 'table', 'customer'],
      },
      {
        id: 'page-analytics',
        kind: 'page',
        label: translateFallback(
          t,
          'sidebar.menuAnalytics',
          'Menu analytics',
        ),
        path: '/menu-analytics',
        icon: BarChart3,
        keywords: ['analytics', 'statistics', 'stats', 'scans'],
      },
      {
        id: 'page-activity',
        kind: 'page',
        label: translateFallback(
          t,
          'sidebar.activityTracking',
          'Activity tracking',
        ),
        path: '/activity-tracking',
        icon: Activity,
        keywords: ['logs', 'activity', 'history'],
      },
      {
        id: 'page-qr-products',
        kind: 'page',
        label: translateFallback(
          t,
          'sidebar.qrPrintProducts',
          'QR products',
        ),
        path: '/qr-print/products',
        icon: Printer,
        keywords: ['print', 'qr', 'nfc'],
      },
      {
        id: 'page-qr-cart',
        kind: 'page',
        label: translateFallback(
          t,
          'sidebar.qrPrintCart',
          'Print cart',
        ),
        path: '/qr-print/cart',
        icon: ShoppingCart,
        keywords: ['print', 'cart', 'basket'],
      },
      {
        id: 'page-qr-orders',
        kind: 'page',
        label: translateFallback(
          t,
          'sidebar.qrPrintOrders',
          'Print orders',
        ),
        path: '/qr-print/orders',
        icon: PackageCheck,
        keywords: ['print', 'orders', 'quotation'],
      },
      {
        id: 'page-users',
        kind: 'page',
        label: translateFallback(t, 'sidebar.users', 'Users'),
        path: '/users',
        icon: Users,
        keywords: ['staff', 'waiter', 'kitchen', 'users'],
      },
      accountItem,
    ];

    const aiMenuImportFeature = getFeature?.('ai_menu_import');

    if (
      aiMenuImportFeature?.visible === true &&
      aiMenuImportFeature?.available === true &&
      aiMenuImportFeature?.locked !== true
    ) {
      items.splice(4, 0, {
        id: 'page-menu-import',
        kind: 'page',
        label: translateFallback(t, 'sidebar.importMenu', 'Import menu'),
        path: '/menu-import',
        icon: MonitorSmartphone,
        keywords: ['ai', 'import', 'scan', 'menu'],
      });
    }

    return items;
  }, [getFeature, searchMode, t]);

  const closeSearch = () => {
    setSearchOpen(false);
    setActiveSearchIndex(0);
  };

  const clearSearch = () => {
    searchRequestRef.current += 1;
    setSearchTerm('');
    setSearchResults([]);
    setSearchLoading(false);
    setActiveSearchIndex(0);
    setSearchOpen(false);
  };

  const openSearch = () => {
    setSearchOpen(true);

    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSearchResult = (result) => {
    if (!result?.path) return;

    clearSearch();
    navigate(result.path);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeSearch();
      searchInputRef.current?.blur();
      return;
    }

    if (!searchResults.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSearchIndex((currentValue) => {
        return (currentValue + 1) % searchResults.length;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSearchIndex((currentValue) => {
        return (
          currentValue - 1 + searchResults.length
        ) % searchResults.length;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchResult(
        searchResults[activeSearchIndex] || searchResults[0],
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        closeSearch();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeSearch();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    closeSearch();
  }, [location.pathname]);

  useEffect(() => {
    const query = searchTerm.trim();
    const normalizedQuery = normalizeSearchText(query);

    if (!query) {
      searchRequestRef.current += 1;
      setSearchResults([]);
      setSearchLoading(false);
      setActiveSearchIndex(0);
      return undefined;
    }

    const pageResults = navigationItems
      .filter((item) => getSearchableText(item).includes(normalizedQuery))
      .map((item) => ({
        ...item,
        title: item.label,
        subtitle: item.path,
        typeLabel: '',
      }));

    if (query.length < 2) {
      searchRequestRef.current += 1;
      setSearchResults(pageResults);
      setSearchLoading(false);
      setActiveSearchIndex(0);
      setSearchOpen(true);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setSearchLoading(true);
    setSearchOpen(true);

    const timeoutId = window.setTimeout(async () => {
      const data = await globalSearchApi.search({
        query,
        mode: searchMode,
        establishmentId,
      });

      if (searchRequestRef.current !== requestId) {
        return;
      }

      const entityResults = [];

      (data.products || []).forEach((product) => {
        entityResults.push({
          id: `product-${product.id}`,
          kind: 'product',
          title:
            getTranslatedField(
              product,
              'name',
              language,
              defaultLocale,
            ) || product.slug || `#${product.id}`,
          subtitle:
            getTranslatedField(
              product.category,
              'name',
              language,
              defaultLocale,
            ) || product.slug || '',
          typeLabel: translateFallback(t, 'sidebar.products', 'Products'),
          path: `/products/${product.id}`,
          icon: UtensilsCrossed,
        });
      });

      (data.categories || []).forEach((category) => {
        entityResults.push({
          id: `category-${category.id}`,
          kind: 'category',
          title:
            getTranslatedField(
              category,
              'name',
              language,
              defaultLocale,
            ) || category.slug || `#${category.id}`,
          subtitle: category.slug || '',
          typeLabel: translateFallback(t, 'sidebar.categories', 'Categories'),
          path: `/categories/${category.id}`,
          icon: FolderTree,
        });
      });

      (data.menus || []).forEach((menuItem) => {
        entityResults.push({
          id: `menu-${menuItem.id}`,
          kind: 'menu',
          title: menuItem.name || menuItem.slug || `#${menuItem.id}`,
          subtitle: menuItem.description || menuItem.slug || '',
          typeLabel: translateFallback(t, 'sidebar.menuDesign', 'Menus'),
          path: `/menus/${menuItem.id}`,
          icon: MonitorSmartphone,
        });
      });

      (data.supplements || []).forEach((supplement) => {
        entityResults.push({
          id: `supplement-${supplement.id}`,
          kind: 'supplement',
          title:
            getTranslatedField(
              supplement,
              'name',
              language,
              defaultLocale,
            ) || `#${supplement.id}`,
          subtitle:
            getTranslatedField(
              supplement,
              'description',
              language,
              defaultLocale,
            ) || '',
          typeLabel: translateFallback(
            t,
            'sidebar.supplements',
            'Supplements',
          ),
          path: `/supplements/${supplement.id}/edit`,
          icon: ListPlus,
        });
      });

      (data.orders || []).forEach((order) => {
        entityResults.push({
          id: `order-${order.id}`,
          kind: 'order',
          title: order.order_number || `#${order.id}`,
          subtitle: [
            order.customer_name,
            order.table_number,
            order.status,
          ]
            .filter(Boolean)
            .join(' · '),
          typeLabel: translateFallback(t, 'sidebar.orders', 'Orders'),
          path: `/orders/${order.id}`,
          icon: ShoppingBag,
        });
      });

      (data.users || []).forEach((record) => {
        entityResults.push({
          id: `user-${record.id}`,
          kind: 'user',
          title: record.name || record.email || `#${record.id}`,
          subtitle: record.email || record.phone || '',
          typeLabel: translateFallback(t, 'sidebar.users', 'Users'),
          path: `/users/${record.id}`,
          icon: Users,
        });
      });

      (data.establishments || []).forEach((establishment) => {
        entityResults.push({
          id: `establishment-${establishment.id}`,
          kind: 'establishment',
          title:
            establishment.name ||
            establishment.slug ||
            `#${establishment.id}`,
          subtitle:
            establishment.business?.name ||
            establishment.owner?.email ||
            establishment.email ||
            '',
          typeLabel: translateFallback(
            t,
            'sidebar.establishments',
            'Establishments',
          ),
          path: `/establishments/${establishment.id}`,
          icon: Store,
        });
      });

      (data.qrProducts || []).forEach((product) => {
        entityResults.push({
          id: `qr-product-${product.id}`,
          kind: 'qrProduct',
          title: product.name || product.slug || `#${product.id}`,
          subtitle: product.product_type || product.description || '',
          typeLabel: translateFallback(
            t,
            'sidebar.qrPrintProducts',
            'QR products',
          ),
          path: `/qr-print/products/${product.id}/customize`,
          icon: Printer,
        });
      });

      (data.qrOrders || []).forEach((order) => {
        entityResults.push({
          id: `qr-order-${order.id}`,
          kind: 'qrOrder',
          title: order.order_number || `#${order.id}`,
          subtitle: order.establishment?.name || order.status || '',
          typeLabel: translateFallback(
            t,
            'sidebar.qrPrintOrders',
            'Print orders',
          ),
          path: `/qr-print/orders/${order.id}`,
          icon: PackageCheck,
        });
      });

      const nextResults = uniqueResults([
        ...pageResults,
        ...entityResults,
      ]).slice(0, 24);

      setSearchResults(nextResults);
      setActiveSearchIndex(0);
      setSearchLoading(false);
    }, 260);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    defaultLocale,
    establishmentId,
    language,
    navigationItems,
    searchMode,
    searchTerm,
    t,
  ]);

  return (
    <div
      ref={searchRef}
      className={`dashboard-header__search dashboard-global-search ${
        searchOpen ? 'dashboard-header__search--open' : ''
      }`}
      onClick={() => {
        if (!searchOpen) {
          openSearch();
        }
      }}
    >
      <button
        type="button"
        className="dashboard-global-search__focus-button"
        onClick={openSearch}
        aria-label={t('header.searchPlaceholder')}
      >
        <Search size={19} />
      </button>

      <input
        ref={searchInputRef}
        type="search"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        onFocus={() => setSearchOpen(true)}
        onKeyDown={handleSearchKeyDown}
        placeholder={t('header.searchPlaceholder')}
        aria-label={t('header.searchPlaceholder')}
        aria-expanded={searchOpen}
        aria-controls="dashboard-global-search-results"
        autoComplete="off"
      />

      {searchLoading && (
        <Loader2
          className="dashboard-global-search__loader"
          size={17}
          aria-label={t('common.loading')}
        />
      )}

      {!searchLoading && searchTerm && (
        <button
          type="button"
          className="dashboard-global-search__clear"
          onClick={(event) => {
            event.stopPropagation();
            clearSearch();
          }}
          aria-label={translateFallback(t, 'common.clear', 'Clear')}
        >
          <X size={16} />
        </button>
      )}

      {searchOpen && searchTerm.trim() && (
        <div
          id="dashboard-global-search-results"
          className="dashboard-global-search__results"
          role="listbox"
        >
          {searchResults.length > 0 ? (
            searchResults.map((result, index) => {
              const ResultIcon = result.icon || Search;
              const active = index === activeSearchIndex;

              return (
                <button
                  key={`${result.kind}-${result.id || result.path}`}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`dashboard-global-search__result ${
                    active
                      ? 'dashboard-global-search__result--active'
                      : ''
                  }`}
                  onMouseEnter={() => setActiveSearchIndex(index)}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSearchResult(result);
                  }}
                >
                  <span className="dashboard-global-search__result-icon">
                    <ResultIcon size={18} />
                  </span>

                  <span className="dashboard-global-search__result-copy">
                    <strong>{result.title}</strong>
                    {result.subtitle && <small>{result.subtitle}</small>}
                  </span>

                  {result.typeLabel && (
                    <span className="dashboard-global-search__result-type">
                      {result.typeLabel}
                    </span>
                  )}
                </button>
              );
            })
          ) : !searchLoading ? (
            <div className="dashboard-global-search__empty">
              <Search size={19} />
              <span>{t('qrPrint.common.empty')}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
